import { ActionIcon, Group, ScrollArea, Space, Stack, useMantineTheme } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { SuperadminLabel } from 'app/Private/Clients/Detail/BalanceTab/BalanceTable/component';
import { QuickActionMenu } from 'app/Private/QuickActionMenu';
import { Logo } from 'components/Logo/Logo';
import { useScrollShadow } from 'hooks/use-scroll-shadow';
import UserMenuButton from 'modules/Navigation/components/UserMenuButton';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { useIsMobile } from 'shared/hooks/utils/use-is-mobile';

import { ActionsSpotlight } from './components/ActionsSpotlight';
import { MobileMenuButton } from './components/MobileMenuButton';
import { NavBackButton } from './components/NavBackButton';
import { NavigationSpotlight } from './components/NavigationSpotlight';
import { NavMenu } from './components/NavMenu';
import { NavMenuItem } from './components/NavMenuItem';
import { SearchSpotlight } from './components/SearchSpotlight';
import { ShortcutHints } from './components/ShortcutHints';
import { ShortcutsModal } from './components/ShortcutsModal';
import { SyncStatus } from './components/SyncStatus';
import { useBuildMenuItems } from './hooks/use-build-menu-items';
import type { Menu } from './types';
import { findMenuForLocation, isMenu } from './utils';

interface NavBarProps {
  closeNavbarOnMobile: () => void;
  onClickOpenNavbar: () => void;
}

export const NavBar = ({ closeNavbarOnMobile, onClickOpenNavbar }: NavBarProps) => {
  const location = useLocation();
  const theme = useMantineTheme();
  const isMobile = useIsMobile();

  const rootMenu = useBuildMenuItems();
  const [currentMenu, setCurrentMenu] = useState<Menu>(
    findMenuForLocation(rootMenu, location.pathname) || rootMenu,
  );

  const { topShadow, bottomShadow, scrollableRef } = useScrollShadow(
    currentMenu.label,
    'light',
    theme.colors.purple[0],
  );

  useEffect(() => {
    setCurrentMenu(findMenuForLocation(rootMenu, location.pathname) ?? rootMenu);
  }, [location.pathname, rootMenu]);

  const { parent: parentMenu } = currentMenu;

  return (
    <>
      <NavigationSpotlight />
      <ActionsSpotlight />
      <SearchSpotlight />
      <ShortcutsModal />
      <Stack bg="purple.0" py="space6" px="space3" h="100%" gap={0} pos="relative">
        <MobileMenuButton onClickOpenNavbar={onClickOpenNavbar} />
        <Group mb="space5" justify="center">
          <Link to="/">
            <Logo variant="fullLogo" height={32} />
          </Link>
          <ActionIcon
            hiddenFrom="mobile"
            color="gray.6"
            variant="transparent"
            onClick={closeNavbarOnMobile}
          >
            <IconX />
          </ActionIcon>
        </Group>
        {parentMenu && (
          <>
            <NavBackButton onClick={() => setCurrentMenu(parentMenu)} label={currentMenu.label} />
            <Space h="space3" />
          </>
        )}
        <ScrollArea flex={1} viewportRef={scrollableRef}>
          {topShadow}
          {bottomShadow}
          {currentMenu.items.map(item =>
            isMenu(item) ? (
              <NavMenu key={item.label} {...item} menu={item} setMenu={setCurrentMenu} />
            ) : (
              <NavMenuItem
                key={item.label}
                {...item}
                onNavigate={isMobile ? closeNavbarOnMobile : () => {}}
              />
            ),
          )}
        </ScrollArea>
        <div id="variants-portal" />
        <Space h="space4" />
        <ShortcutHints />
        <Space h="space3" />
        <SyncStatus />
        <Space h="space3" />
        <QuickActionMenu />
        <Space h="space6" />
        <UserMenuButton />
        <SuperadminLabel />
      </Stack>
    </>
  );
};
